import {
  call,
  select,
  delay,
  put,
} from 'redux-saga/effects';
import cookies from 'js-cookie';

import { initiateIdentification } from '@apiClient/authorization';
import { initiateSignDocument, signDocument } from '@apiClient/document';
import { userAuthenticate } from '@apiClient/user';
import { bankIdTypes, bankIdRequests, BANK_ID_TIMEOUT } from '@constants/bankId';
import {
  bankId,
  bankIdSuccess,
  bankIdFailure,
  storeBankIdInstructions,
  setBankIdTimeout,
} from '@redux/actions/auth';

const getBankIdType = (state) => state.auth.bankIdType;

const bankIdCollectMethods = {
  [bankIdRequests.LOGIN]: undefined,
  [bankIdRequests.SIGN_DOCUMENT]: signDocument,
};

const bankIdInitialisationMethods = {
  [bankIdRequests.LOGIN]: initiateIdentification,
  [bankIdRequests.SIGN_DOCUMENT]: initiateSignDocument,
};

function* bankIdIdentify(token, action, payload, signingPartyType) {
  let res = { pending: true };
  let hasTimedOut = false;

  while (res.pending && !hasTimedOut) {
    yield delay(1000);
    try {
      res = yield call(userAuthenticate, payload, token.orderRef);
      yield put(storeBankIdInstructions(res.data));
      const timeout = yield select((state) => state.auth.timeout);
      hasTimedOut = timeout < Date.now();
    } catch (e) {
      if (!e.message.startsWith('Request has been terminated')) throw e;
    }
  }

  if (hasTimedOut) {
    const bankIdType = yield select(getBankIdType);
    const error = new Error('START_FAILED');
    error.bankIdType = bankIdType;

    throw error;
  }

  const { token: jwt } = res.data;
  if (jwt) {
    cookies.set('token', jwt, {
      secure: process.env.NODE_ENV === 'production',
      SameSite: 'Lax',
    });
  }

  if (bankIdCollectMethods[action]) {
    res = yield call(bankIdCollectMethods[action], {
      ...payload,
      signingPartyType,
    });
    return res;
  }

  return {};
}

function* bankIdSign(token, action, payload, signingPartyType) {
  let res = { pending: true };
  let hasTimedOut = false;

  const bankIdType = yield select(getBankIdType);

  while (res.pending && !hasTimedOut) {
    yield delay(1000);

    try {
      res = yield call(
        bankIdCollectMethods[action],
        { ...payload, bankIdType, signingPartyType },
        token.orderRef,
      );
      yield put(storeBankIdInstructions(res.data));

      const timeout = yield select((state) => state.auth.timeout);
      hasTimedOut = timeout < Date.now();
    } catch (e) {
      if (!e.message.startsWith('Request has been terminated')) throw e;
    }
  }

  if (hasTimedOut) {
    const error = new Error('START_FAILED');
    error.bankIdType = bankIdType;

    throw error;
  }

  return res;
}

const handleBankIdMethods = {
  [bankIdRequests.LOGIN]: bankIdIdentify,
  [bankIdRequests.SIGN_DOCUMENT]: bankIdSign,
};

function* bankIdSagaFlow({ payload, action }) {
  try {
    const signingPartyType = yield select((state) => state.signup.signingPartyType);

    const initialisationMethod = bankIdInitialisationMethods[action];
    if (!initialisationMethod) throw new Error(`Invalid BankID request. Received ${action}, expected ${Object.values(bankIdRequests).join(', ')}`);

    yield put(bankId());
    const bankIdType = yield select(getBankIdType);
    const initializationResponse = yield call(initialisationMethod, {
      ...payload,
      signingPartyType,
      bankIdType,
    });

    if (bankIdType === bankIdTypes.SAME_DEVICE) {
      global.location = `bankid:///?autostarttoken=${initializationResponse.autoStartToken}&redirect=null`;
    }

    const bankIdMethod = handleBankIdMethods[action];
    const res = yield bankIdMethod(
      initializationResponse,
      action,
      { ...payload, bankIdType, transactionId: initializationResponse.transactionId },
      signingPartyType,
    );

    return { refreshBankIdFlow: false, data: res.data };
  } catch (e) {
    if (e.response && e.response.body
      && ((e.response.body.errors && e.response.body.errors.includes('START_FAILED'))
      || e.response.body.errorCode === 'START_FAILED')) {
      return { refreshBankIdFlow: true };
    }

    throw e;
  }
}

export function* startBankIdTimeoutSaga() {
  const timeout = BANK_ID_TIMEOUT;
  const endTime = Date.now() + timeout;

  yield put(setBankIdTimeout(endTime));
}

export default function* bankIdSaga({ payload, action }) {
  try {
    yield call(startBankIdTimeoutSaga);

    let refreshBankIdFlow = true;
    let res;

    while (refreshBankIdFlow) {
      res = yield call(bankIdSagaFlow, { payload, action });
      refreshBankIdFlow = res.refreshBankIdFlow;
    }

    yield put(bankIdSuccess());

    return res.data;
  } catch (e) {
    yield put(bankIdFailure());
    throw e;
  }
}
